import React, { useEffect } from 'react';
import { CircleCheckIcon, FileIcon, X, Zap, AlertTriangle } from 'lucide-react';
import { Button } from '../Button';
import type { QuestionnaireType } from '../../../hooks/useHotels';
import styled from 'styled-components';

const ModalOverlay = styled.div<{ $scrollTop: number }>`
  position: absolute;
  top: ${({ $scrollTop }) => $scrollTop}px;
  left: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.4);
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  background-color: var(--color-white);
  padding: 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  max-width: 62rem;
  width: 100%;
  max-height: 80vh;
  overflow-y: auto;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem;
  border: none;
  background: none;
  border-radius: 9999px;
  transition: background-color 0.2s;
  cursor: pointer;

  &:hover {
    background-color: var(--color-gray-100);
  }
`;

const CardsContainer = styled.div`
  display: flex;
  align-items: stretch;
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const CardWrapper = styled.div<{ $borderColor: string; $isCurrent: boolean }>`
  background-color: var(--color-white);
  border-radius: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.07);
  transition: all 0.2s ease;
  border: ${({ $borderColor }) => `2px solid ${$borderColor}`};
  border-top: ${({ $borderColor }) => `4px solid ${$borderColor}`};
`;

const CardHeader = styled.div<{ $headerBg: string; $topBorderColor: string }>`
  //   background: ${({ $headerBg }) => $headerBg};
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1.5rem 0 1.5rem;
  //   border: ${({ $topBorderColor }) => $topBorderColor};
`;

const IconContainer = styled.div<{ $iconBg: string }>`
  width: 2.75rem;
  height: 2.75rem;
  background-color: ${({ $iconBg }) => $iconBg};
  border-radius: 0.625rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-white);
  margin-bottom: 0.875rem;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: var(--font-weight-bold);
  color: var(--color-gray-900);
  margin: 0 0 0.5rem 0;
`;

const CardDescription = styled.p`
  color: var(--color-gray-600);
  font-size: 0.875rem;
  margin: 0 0 1.25rem 0;
  line-height: 1.5;
`;

const IncludesLabel = styled.p`
  color: var(--color-gray-700);
  font-weight: var(--font-weight-semibold);
  font-size: 0.9375rem;
  margin: 1.25rem 1.5rem 0.75rem 1.5rem;
`;

const FeaturesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-bottom: 1.5rem;
  flex: 1;
  padding: 0 1.5rem;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
`;

const FeatureText = styled.span`
  color: var(--color-gray-700);
  font-size: 0.875rem;
  line-height: 1.4;
`;

const ButtonContainer = styled.div`
  padding: 0 1.5rem 1.5rem 1.5rem;
`;

interface SwitchAssessmentPathModalProps {
  currentPath: QuestionnaireType;
  isLoading: boolean;
  onSwitch: (path: QuestionnaireType) => void;
  onClose: () => void;
}

const cardConfigs = [
  {
    id: 'FASTTRACK' as QuestionnaireType,
    icon: <Zap size={20} />,
    title: 'Fundamentals',
    description:
      'A great place to start. Get your initial sustainability snapshot with a focused set of questions.',
    features: [
      '36 core questions',
      'Takes about 30 minutes',
      'Sustainability insights and scoring overview',
      'Strengthen your profile with the full assessment anytime',
    ],
    switchButtonText: 'Switch to Fundamentals',
    borderColor: '#3B82F6',
    bgColor: '#ffffff',
    headerBg: 'linear-gradient(to bottom, #DBEAFE, #ffffff)',
    borderTop: '4px solid #3B82F6',
    iconBg: '#3B82F6',
    buttonBorderColor: '#c8cacc',
  },
  {
    id: 'FULL' as QuestionnaireType,
    icon: <FileIcon size={20} />,
    title: 'Full Assessment',
    description:
      'Go deeper from the start. Complete the full evaluation for the most comprehensive results.',
    features: [
      '100+ in-depth questions',
      'Takes about 2 hours',
      'Access to Boost upgrade for shareable link and competitive intelligence',
      'Review and confirm all responses',
    ],
    switchButtonText: 'Switch to Full Assessment',
    borderColor: '#22C55E',
    bgColor: '#ffffff',
    headerBg: 'linear-gradient(to bottom, #DCFCE7, #ffffff)',
    iconBg: '#22C55E',
    borderTop: '4px solid #22C55E',
  },
];

const SwitchAssessmentPathModal = ({
  currentPath,
  isLoading,
  onSwitch,
  onClose,
}: SwitchAssessmentPathModalProps) => {
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const mainElement = document.querySelector('main');
    const originalMainOverflow = mainElement?.style.overflow;

    document.body.style.overflow = 'hidden';
    if (mainElement) {
      mainElement.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      if (mainElement && originalMainOverflow !== undefined) {
        mainElement.style.overflow = originalMainOverflow;
      } else if (mainElement) {
        mainElement.style.overflow = 'auto';
      }
    };
  }, []);

  const getScrollTop = () => {
    const mainElement = document.querySelector('main');
    return mainElement?.scrollTop || 0;
  };

  return (
    <ModalOverlay $scrollTop={getScrollTop()} onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close dialog">
          <X className="w-5 h-5 text-gray-500" />
        </CloseButton>

        <div className="flex items-center gap-2 mb-1">
          <AlertTriangle size={22} className="text-amber-500" />
          <h2 className="text-2xl font-bold text-gray-900">
            Switch Your Assessment Path?
          </h2>
        </div>
        <p className="text-gray-500 text-sm mt-1">
          You've already started answering questions. Switching paths won't
          delete your progress — your responses will be saved and available if
          you switch back.
        </p>

        <CardsContainer>
          {cardConfigs.map(config => {
            const isCurrent = config.id === currentPath;
            return (
              <CardWrapper
                key={config.id}
                $borderColor={config.borderColor}
                $isCurrent={isCurrent}
              >
                <CardHeader
                  $headerBg={config.headerBg}
                  $topBorderColor={config.borderTop}
                >
                  <IconContainer $iconBg={config.iconBg}>
                    {config.icon}
                  </IconContainer>
                  <CardTitle>{config.title}</CardTitle>
                  <CardDescription>{config.description}</CardDescription>
                </CardHeader>

                <IncludesLabel>Includes:</IncludesLabel>

                <FeaturesContainer>
                  {config.features.map((feature, idx) => (
                    <FeatureItem key={idx}>
                      <CircleCheckIcon
                        size={16}
                        className="mt-0.5 shrink-0"
                        stroke="var(--color-planet-green)"
                      />
                      <FeatureText>{feature}</FeatureText>
                    </FeatureItem>
                  ))}
                </FeaturesContainer>

                <ButtonContainer>
                  {isCurrent ? (
                    <Button
                      size="lg"
                      onClick={onClose}
                      className="w-full flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: '#dfdcdc',
                        border: `2px solid ${config.buttonBorderColor}`,
                        color: '#000000',
                      }}
                    >
                      <span
                        className="w-2 h-2 rounded-full mr-2 inline-block"
                        style={{ backgroundColor: config.borderColor }}
                      />
                      Current Path
                    </Button>
                  ) : (
                    <Button
                      size="lg"
                      onClick={() => onSwitch(config.id)}
                      loading={isLoading}
                      disabled={isLoading}
                      className="w-full"
                      style={{
                        backgroundColor: config.borderColor,
                        borderColor: config.borderColor,
                        color: '#ffffff',
                      }}
                    >
                      {isLoading ? 'Switching...' : config.switchButtonText}
                    </Button>
                  )}
                </ButtonContainer>
              </CardWrapper>
            );
          })}
        </CardsContainer>

        {/* Amber warning banner */}
        <div className="mt-5 flex items-start gap-2.5 rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <AlertTriangle size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-800 text-sm">
            Any in-progress questions on your current path will be saved. You
            can return to them at any time.
          </p>
        </div>

        {/* Cancel link */}
        <div className="mt-4 flex justify-center">
          <button
            onClick={onClose}
            className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
          >
            Cancel — keep my current path
          </button>
        </div>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SwitchAssessmentPathModal;
